/**
 * AliExpress API Service
 *
 * Service for interacting with AliExpress Affiliate API
 * Documentation: https://developers.aliexpress.com
 */

import type {
  AliExpressProduct,
  AliExpressSearchParams,
  AliExpressSearchResponse,
  AliExpressProductDetail,
  AliExpressHotProduct,
  AliExpressHotProductsResponse,
  AliExpressCategory,
  AliExpressAffiliateLink,
} from '../types';
import { ALIEXPRESS_COMPUTER_CATEGORIES } from '../types';

// API Configuration
const API_BASE_URL = '/api/aliexpress';

/**
 * Search products on AliExpress
 */
export async function searchProducts(
  params: AliExpressSearchParams
): Promise<AliExpressSearchResponse> {
  const searchParams = new URLSearchParams();

  searchParams.set('keywords', params.keywords);

  if (params.categoryId) {
    searchParams.set('categoryId', params.categoryId);
  }
  if (params.sort) {
    searchParams.set('sort', params.sort);
  }
  if (params.pageNo) {
    searchParams.set('pageNo', params.pageNo.toString());
  }
  if (params.pageSize) {
    searchParams.set('pageSize', params.pageSize.toString());
  }
  if (params.minPrice !== undefined) {
    searchParams.set('minPrice', params.minPrice.toString());
  }
  if (params.maxPrice !== undefined) {
    searchParams.set('maxPrice', params.maxPrice.toString());
  }
  if (params.shipToCountry) {
    searchParams.set('shipToCountry', params.shipToCountry);
  }
  if (params.deliveryDays) {
    searchParams.set('deliveryDays', params.deliveryDays.toString());
  }

  const response = await fetch(`${API_BASE_URL}/search?${searchParams.toString()}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Search failed' }));
    throw new Error(error.message || 'Failed to search products');
  }

  return response.json();
}

/**
 * Get product details by ID
 */
export async function getProductDetail(productId: string): Promise<AliExpressProductDetail> {
  const response = await fetch(`${API_BASE_URL}/product/${productId}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to get product' }));
    throw new Error(error.message || 'Failed to get product details');
  }

  return response.json();
}

/**
 * Get hot/trending products
 */
export async function getHotProducts(
  categoryId?: string,
  limit: number = 20
): Promise<AliExpressHotProductsResponse> {
  const params = new URLSearchParams();

  if (categoryId) {
    params.set('categoryId', categoryId);
  }
  params.set('limit', limit.toString());

  const response = await fetch(`${API_BASE_URL}/hot?${params.toString()}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to get hot products' }));
    throw new Error(error.message || 'Failed to get hot products');
  }

  return response.json();
}

/**
 * Get product categories
 */
export async function getCategories(
  parentId?: string
): Promise<AliExpressCategory[]> {
  const params = new URLSearchParams();

  if (parentId) {
    params.set('parentId', parentId);
  }

  const response = await fetch(`${API_BASE_URL}/categories?${params.toString()}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to get categories' }));
    throw new Error(error.message || 'Failed to get categories');
  }

  return response.json();
}

/**
 * Generate affiliate link for a product
 */
export async function generateAffiliateLink(
  productId: string,
  productUrl: string
): Promise<AliExpressAffiliateLink> {
  const response = await fetch(`${API_BASE_URL}/affiliate-link`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ productId, productUrl }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to generate link' }));
    throw new Error(error.message || 'Failed to generate affiliate link');
  }

  return response.json();
}

/**
 * Get products by category (for computer products)
 */
export async function getProductsByCategory(
  categoryId: string,
  options?: {
    sort?: AliExpressSearchParams['sort'];
    pageNo?: number;
    pageSize?: number;
    minPrice?: number;
    maxPrice?: number;
  }
): Promise<AliExpressSearchResponse> {
  return searchProducts({
    keywords: '',
    categoryId,
    ...options,
  });
}

/**
 * Search computer products (desktops, laptops, components)
 */
export async function searchComputerProducts(
  keywords: string,
  subCategory?: keyof typeof ALIEXPRESS_COMPUTER_CATEGORIES,
  options?: Omit<AliExpressSearchParams, 'keywords' | 'categoryId'>
): Promise<AliExpressSearchResponse> {
  const categoryId = subCategory
    ? String(ALIEXPRESS_COMPUTER_CATEGORIES[subCategory])
    : undefined;

  return searchProducts({
    keywords,
    categoryId,
    ...options,
  });
}

/**
 * Get trending computer products
 */
export async function getTrendingComputerProducts(
  limit: number = 10
): Promise<AliExpressHotProduct[]> {
  const response = await getHotProducts(
    String(ALIEXPRESS_COMPUTER_CATEGORIES.COMPUTERS),
    limit
  );
  return response.products;
}

/**
 * Convert AliExpress product to internal Product format
 */
export function convertAliExpressToProduct(item: AliExpressProduct): {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  images: Array<{ url: string; alt: string }>;
  prices: Array<{
    price: number;
    originalPrice?: number;
    affiliateUrl: string;
    store: { name: string };
  }>;
  rating: number;
  reviewCount: number;
  category: string;
  brand: string;
} {
  return {
    id: item.productId,
    name: item.productTitle,
    nameEn: item.productTitle,
    description: `${item.productTitle} - ${item.shopName}`,
    images: [{ url: item.imageUrl, alt: item.productTitle }],
    prices: [{
      price: item.salePrice,
      originalPrice: item.originalPrice > item.salePrice ? item.originalPrice : undefined,
      affiliateUrl: item.productUrl,
      store: { name: 'AliExpress' },
    }],
    rating: item.evaluateScore,
    reviewCount: item.volume,
    category: item.categoryName || item.firstLevelCategoryName,
    brand: item.shopName,
  };
}

/**
 * Format price with currency
 */
export function formatPrice(price: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('ko-KR', {
    style: 'currency',
    currency,
  }).format(price);
}

/**
 * Get KRW price (API already returns prices in KRW, converted from CNY × 190)
 * Previously multiplied by exchange rate, but now API handles conversion
 */
export function estimateKrwPrice(krwPrice: number): number {
  return Math.round(krwPrice);
}

/**
 * Format delivery time
 */
export function formatDeliveryTime(deliveryDays: string): string {
  // Parse AliExpress delivery format (e.g., "15-30 days")
  const match = deliveryDays.match(/(\d+)-?(\d+)?/);
  if (match) {
    const min = parseInt(match[1], 10);
    const max = match[2] ? parseInt(match[2], 10) : min;
    if (min === max) {
      return `${min}일`;
    }
    return `${min}-${max}일`;
  }
  return deliveryDays;
}

export default {
  searchProducts,
  getProductDetail,
  getHotProducts,
  getCategories,
  generateAffiliateLink,
  getProductsByCategory,
  searchComputerProducts,
  getTrendingComputerProducts,
  convertAliExpressToProduct,
  formatPrice,
  estimateKrwPrice,
  formatDeliveryTime,
};
